import { useState, useEffect, useCallback } from 'react'
import { Loader2, DollarSign, Users, Filter } from 'lucide-react'
import { useT } from '../../hooks/useTranslation'
import { getAdminStats } from '../../services/api'

const NETWORK_LABELS = {
  TRC20: 'USDT · TRC-20',
  BEP20: 'USDT · BEP-20',
  SOL_USDT: 'USDT · SOL',
  TON: 'USDT · TON',
  stars: '⭐ Telegram Stars',
}

const fmtMoney = (x) => `$${Number(x || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function Card({ title, icon, accent = '#34D399', children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
        <span style={{ display: 'flex', color: accent }}>{icon}</span>
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

function Row({ label, value, valueColor = '#fff', valueSub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 2px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.35, flex: 1 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: valueColor, textAlign: 'right', flexShrink: 0 }}>
        {value}
        {valueSub && <span style={{ fontSize: 11, color: '#636366', fontWeight: 600, marginLeft: 5 }}>{valueSub}</span>}
      </span>
    </div>
  )
}

function BigStat({ label, value, color = '#34D399' }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: '#636366', marginBottom: 4, letterSpacing: '0.02em', lineHeight: 1.3 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  )
}

export default function StatisticsSection() {
  const t = useT()

  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const refresh = useCallback(() => {
    getAdminStats()
      .then(s => setStats(s))
      .catch(e => setError(e?.message || t.adminSubsError))
      .finally(() => setLoading(false))
  }, [t.adminSubsError])

  // Статистика живая: обновляем раз в минуту и при возврате фокуса в окно.
  // Раньше грузилась один раз при открытии панели и «замирала» на старых
  // значениях (например «заходили сегодня» на прежнюю дату).
  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 60_000)
    window.addEventListener('focus', refresh)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', refresh)
    }
  }, [refresh])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '28px 0', color: '#636366', fontSize: 13 }}>
        <Loader2 size={16} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
        …
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div style={{ fontSize: 12.5, color: '#F87171', padding: '16px 2px', lineHeight: 1.5 }}>⚠️ {error || t.adminSubsError}</div>
    )
  }

  const { revenue, users, funnel } = stats

  return (
    <div style={{ padding: '4px 14px 8px' }}>
      {/* ── Выручка ───────────────────────────────────────────────────────── */}
      <Card title={t.statRevenue} icon={<DollarSign size={15} />} accent="#34D399">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <BigStat label={t.statRevenueTotal} value={fmtMoney(revenue.total)} />
          <BigStat label={t.statRevenueMonth} value={fmtMoney(revenue.month)} color="#F59E0B" />
          <BigStat label={t.statRevenueWeek} value={fmtMoney(revenue.week)} color="#818CF8" />
        </div>

        {/* Сброс статистики в «Финансах» затрагивает и выручку (осознанное
            решение владельца — иначе два раздела показывали бы разные суммы).
            Плашка объясняет, почему «Общая выручка» меньше ожидаемой. */}
        {revenue.resetAt ? (
          <div style={{
            fontSize: 11, color: '#F59E0B', lineHeight: 1.45,
            background: 'rgba(245,158,11,0.08)', borderRadius: 10,
            padding: '7px 10px', marginBottom: 10,
          }}>
            {t.statRevenueReset.replace('{at}', revenue.resetAt)}
          </div>
        ) : null}

        {(revenue.byPlan && revenue.byPlan.length > 0) && (
          <div style={{ fontSize: 11, color: '#636366', margin: '2px 0 6px', letterSpacing: '0.02em' }}>{t.statByPlan}</div>
        )}
        {revenue.byPlan && revenue.byPlan.map(r => (
          <Row
            key={r.code}
            label={r.title || r.code}
            value={fmtMoney(r.amount)}
            valueSub={`${r.count} ${t.statCountSuffix}`}
          />
        ))}

        {(revenue.byNetwork && revenue.byNetwork.length > 0) && (
          <div style={{ fontSize: 11, color: '#636366', margin: '12px 0 6px', letterSpacing: '0.02em' }}>{t.statByNetwork}</div>
        )}
        {revenue.byNetwork && revenue.byNetwork.map(r => (
          <Row
            key={r.network}
            label={NETWORK_LABELS[r.network] || r.network}
            value={fmtMoney(r.amount)}
            valueSub={`${r.count} ${t.statCountSuffix}`}
          />
        ))}
        {(!revenue.byPlan || revenue.byPlan.length === 0) && (
          <div style={{ fontSize: 12.5, color: '#636366', padding: '4px 2px' }}>—</div>
        )}
      </Card>

      {/* ── Пользователи ─────────────────────────────────────────────────── */}
      <Card title={t.statUsers} icon={<Users size={15} />} accent="#818CF8">
        {/* Строка «Активны сейчас» убрана: backend-метрика users.active = is_active=1
            означает лишь «не заблокировал бота» и почти всегда равна «всего
            зарегистрировано» — вводит в заблуждение. Реальную активность
            показывают строки ниже (заходили сегодня / 7 / 30 дней). */}
        <Row label={t.statUsersTotal} value={users.total} valueColor="#fff" />
        <Row label={t.statSeenToday} value={users.seenToday} valueColor="#F59E0B" />
        <Row label={t.statSeen7d} value={users.seen7d} valueColor="#F59E0B" />
        <Row label={t.statSeen30d} value={users.seen30d} valueColor="#F59E0B" />
        <Row label={t.statWithApiKeys} value={users.withApiKeys} valueColor="#818CF8" />
        <Row label={t.statWithBots} value={users.withBots} valueColor="#818CF8" />
        <Row label={t.statOnDemo} value={users.onDemo} valueColor="#F59E0B" />
        <Row label={t.statOnReal} value={users.onReal} valueColor="#34D399" />
      </Card>

      {/* ── Воронка «посмотрели, но не дошли до оплаты» ──────────────────── */}
      <Card title={t.statFunnel} icon={<Filter size={15} />} accent="#F59E0B">
        <Row label={t.statViewedApp} value={funnel.viewedApp} valueColor="#8E8E93" />
        <Row label={t.statNotReachedPayment} value={funnel.notReachedPayment} valueColor="#F87171" />
        <Row label={t.statStartedNotPaid} value={funnel.startedNotPaid} valueColor="#F59E0B" />
        <Row label={t.statEverPaid} value={funnel.everPaid} valueColor="#34D399" />
        <Row label={t.statNoSubscription} value={funnel.noSubscription} valueColor="#F87171" />
      </Card>
    </div>
  )
}
